import React, { useMemo, useState } from 'react';
import { MeetingMember } from '../../services/meetingService';
import {
  collectMemberEmails,
  formatInterestDate,
  formatInterestResponsesDetail,
} from '../../data/workshop2026InterestSurvey';
import { formatNameFirstLastInitial } from '../../utils/userState';
import styles from '../../styles/meetingSettingsPage.module.css';

interface WorkshopParticipantsAdminProps {
  members: MeetingMember[];
  canEdit: boolean;
  onMessage: (msg: string) => void;
  onError: (msg: string) => void;
}

interface SignupByDateRow {
  dateKey: string;
  label: string;
  confirmed: number;
  interest: number;
}

/** Local calendar date key (YYYY-MM-DD) — same local-timezone rule as formatInterestDate. */
function localDateKey(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function signedUpDate(member: MeetingMember): string {
  if (member.registration_state === 'confirmed' && member.confirmed_at) {
    return formatInterestDate(member.confirmed_at) || '—';
  }
  if (member.registration_state === 'interest' && member.interest_at) {
    return formatInterestDate(member.interest_at) || '—';
  }
  return '—';
}

function phoneListLabel(member: MeetingMember): string {
  if (!member.include_on_printed_phone_list) return 'Not on phone list';
  if (member.sponsor_willingness === 'yes') return 'On phone list · Sponsor';
  if (member.sponsor_willingness === 'temporary') return 'On phone list · Temp sponsor';
  return 'On phone list';
}

function sortByName(a: MeetingMember, b: MeetingMember): number {
  return (a.display_name || '').localeCompare(b.display_name || '', undefined, {
    sensitivity: 'base',
  });
}

/** Confirmed → confirmed_at; Interested → interest_at. Missing dates sort last. */
function signupTimestamp(member: MeetingMember): number {
  const iso =
    member.registration_state === 'confirmed'
      ? member.confirmed_at
      : member.registration_state === 'interest'
        ? member.interest_at
        : null;
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/** Newest signup first, then name A–Z. */
function sortBySignupDateThenName(a: MeetingMember, b: MeetingMember): number {
  const byDate = signupTimestamp(b) - signupTimestamp(a);
  if (byDate !== 0) return byDate;
  return sortByName(a, b);
}

function formatByDateLine(row: SignupByDateRow): string {
  const parts: string[] = [];
  if (row.confirmed > 0) {
    parts.push(`${row.confirmed} Confirmed`);
  }
  if (row.interest > 0) {
    parts.push(`${row.interest} Interested`);
  }
  return `${row.label} — ${parts.join(', ')}`;
}

const WorkshopParticipantsAdmin: React.FC<WorkshopParticipantsAdminProps> = ({
  members,
  canEdit,
  onMessage,
  onError,
}) => {
  const [confirmedOpen, setConfirmedOpen] = useState(false);
  const [interestOpen, setInterestOpen] = useState(false);
  const [byDateOpen, setByDateOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [emailPreview, setEmailPreview] = useState<'interest' | 'confirmed' | null>(null);

  const confirmedMembers = useMemo(
    () =>
      members
        .filter((m) => m.registration_state === 'confirmed')
        .sort(sortBySignupDateThenName),
    [members]
  );
  const interestMembers = useMemo(
    () =>
      members
        .filter((m) => m.registration_state === 'interest')
        .sort(sortBySignupDateThenName),
    [members]
  );

  const signupsByDate = useMemo(() => {
    const buckets = new Map<string, { confirmed: number; interest: number; label: string }>();
    for (const m of members) {
      let iso: string | null | undefined;
      let kind: 'confirmed' | 'interest' | null = null;
      if (m.registration_state === 'confirmed' && m.confirmed_at) {
        iso = m.confirmed_at;
        kind = 'confirmed';
      } else if (m.registration_state === 'interest' && m.interest_at) {
        iso = m.interest_at;
        kind = 'interest';
      }
      if (!iso || !kind) continue;
      const key = localDateKey(iso);
      if (!key) continue;
      const existing = buckets.get(key);
      if (existing) {
        existing[kind] += 1;
      } else {
        buckets.set(key, {
          confirmed: kind === 'confirmed' ? 1 : 0,
          interest: kind === 'interest' ? 1 : 0,
          label: formatInterestDate(iso) || key,
        });
      }
    }
    return Array.from(buckets.entries())
      .map(([dateKey, v]) => ({
        dateKey,
        label: v.label,
        confirmed: v.confirmed,
        interest: v.interest,
      }))
      .sort((a, b) => b.dateKey.localeCompare(a.dateKey));
  }, [members]);

  const interestEmails = useMemo(
    () => collectMemberEmails(members, 'interest'),
    [members]
  );
  const confirmedEmails = useMemo(
    () => collectMemberEmails(members, 'confirmed'),
    [members]
  );

  const copyEmails = async (list: 'interest' | 'confirmed') => {
    const emails = list === 'interest' ? interestEmails : confirmedEmails;
    if (emails.length === 0) {
      onError(`No ${list} list emails to copy.`);
      return;
    }
    const text = emails.join(', ');
    try {
      await navigator.clipboard.writeText(text);
      onMessage(
        `Copied ${emails.length} ${list === 'interest' ? 'interest' : 'confirmed'} email${emails.length === 1 ? '' : 's'}. Paste into Gmail To or Bcc.`
      );
      setEmailPreview(list);
    } catch {
      setEmailPreview(list);
      onMessage('Select the email list below and copy manually.');
    }
  };

  const previewEmails = emailPreview === 'interest' ? interestEmails : confirmedEmails;

  const renderMemberTable = (list: MeetingMember[], statusClass: string, statusLabel: string) => {
    if (list.length === 0) {
      return <p className={styles.membersEmpty}>None yet.</p>;
    }
    return (
      <div className={styles.participantTableWrap}>
        <table className={`${styles.participantTable} ${styles.participantTableCompact}`}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th>Signed up</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.map((m) => {
              const detail = formatInterestResponsesDetail(m.interest_responses);
              const expanded = expandedId === m.user_id;
              const displayName = formatNameFirstLastInitial(m.display_name, m.email || 'Member');
              return (
                <React.Fragment key={m.user_id}>
                  <tr>
                    <td>
                      {displayName}
                      {m.is_admin && <span className={styles.adminBadgeInline}>Admin</span>}
                    </td>
                    <td>
                      <span className={statusClass}>{statusLabel}</span>
                    </td>
                    <td>{signedUpDate(m)}</td>
                    <td className={styles.participantActionsCell}>
                      <button
                        type="button"
                        className={styles.linkishBtn}
                        onClick={() => setExpandedId(expanded ? null : m.user_id)}
                      >
                        {expanded ? 'Hide' : 'Details'}
                      </button>
                    </td>
                  </tr>
                  {expanded && (
                    <tr className={styles.participantDetailRow}>
                      <td colSpan={4}>
                        <dl className={styles.questionnaireDetailList}>
                          <div className={styles.questionnaireDetailItem}>
                            <dt>Email</dt>
                            <dd>{m.email || '—'}</dd>
                          </div>
                          <div className={styles.questionnaireDetailItem}>
                            <dt>Phone list</dt>
                            <dd>{phoneListLabel(m)}</dd>
                          </div>
                          {detail.map((row) => (
                            <div key={row.label} className={styles.questionnaireDetailItem}>
                              <dt>{row.label}</dt>
                              <dd>{row.value}</dd>
                            </div>
                          ))}
                        </dl>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <section className={styles.settingsSection}>
      <h2 className={styles.sectionTitle}>Participants</h2>
      <p className={styles.fieldHint}>
        {confirmedMembers.length} confirmed · {interestMembers.length} on interest list
      </p>

      {canEdit && (
        <div className={styles.participantExportRow}>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={() => copyEmails('interest')}
            disabled={interestEmails.length === 0}
          >
            Copy interest emails ({interestEmails.length})
          </button>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={() => copyEmails('confirmed')}
            disabled={confirmedEmails.length === 0}
          >
            Copy confirmed emails ({confirmedEmails.length})
          </button>
        </div>
      )}

      {emailPreview && previewEmails.length > 0 && (
        <div className={styles.emailExportPreview}>
          <label className={styles.label}>
            {emailPreview === 'interest' ? 'Interest list emails' : 'Confirmed list emails'}
          </label>
          <textarea
            className={styles.textarea}
            rows={3}
            readOnly
            value={previewEmails.join(', ')}
            onFocus={(e) => e.target.select()}
          />
          <button
            type="button"
            className={styles.linkishBtn}
            onClick={() => setEmailPreview(null)}
          >
            Hide
          </button>
        </div>
      )}

      <div className={styles.participantAccordion}>
        <button
          type="button"
          className={styles.participantAccordionHeader}
          aria-expanded={confirmedOpen}
          onClick={() => setConfirmedOpen((o) => !o)}
        >
          <span className={styles.participantAccordionChevron} aria-hidden>
            {confirmedOpen ? '▾' : '▸'}
          </span>
          Confirmed ({confirmedMembers.length})
        </button>
        {confirmedOpen && (
          <div className={styles.participantAccordionBody}>
            {renderMemberTable(confirmedMembers, styles.statusConfirmed, 'Confirmed')}
          </div>
        )}

        <button
          type="button"
          className={styles.participantAccordionHeader}
          aria-expanded={interestOpen}
          onClick={() => setInterestOpen((o) => !o)}
        >
          <span className={styles.participantAccordionChevron} aria-hidden>
            {interestOpen ? '▾' : '▸'}
          </span>
          Interested ({interestMembers.length})
        </button>
        {interestOpen && (
          <div className={styles.participantAccordionBody}>
            {renderMemberTable(interestMembers, styles.statusInterest, 'Interest')}
          </div>
        )}

        <button
          type="button"
          className={styles.participantAccordionHeader}
          aria-expanded={byDateOpen}
          onClick={() => setByDateOpen((o) => !o)}
        >
          <span className={styles.participantAccordionChevron} aria-hidden>
            {byDateOpen ? '▾' : '▸'}
          </span>
          Sign-ups by date ({signupsByDate.length} day{signupsByDate.length === 1 ? '' : 's'})
        </button>
        {byDateOpen && (
          <div className={styles.participantAccordionBody}>
            {signupsByDate.length === 0 ? (
              <p className={styles.membersEmpty}>No sign-up dates yet.</p>
            ) : (
              <ul className={styles.signupsByDateList}>
                {signupsByDate.map((row) => (
                  <li key={row.dateKey}>{formatByDateLine(row)}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default WorkshopParticipantsAdmin;
